/* SIZESOFTX
=========================================================================================
* Descripción: Licencias / Tenants (listado, detalle, cupo MaxDevices, equipos, solicitudes)
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 12/09/2026 | Lic. José Luis Aragón Cervantes  | Reactivar licencia, eliminar equipo, correo de contrato
*      3       | 02/10/2026 | Muse Spark                       | Herramienta Extender vigencia (días o fecha
*              |            |                                  | explícita) con referencia de pago para futura
*              |            |                                  | automatización por reportes de pagos.
* ---------------------------------------------------------------------------------------
*/

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  MailOutline as MailOutlineIcon,
  Refresh as RefreshIcon,
  RemoveCircle as RemoveCircleIcon,
  VerifiedUser as VerifiedUserIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import {
  apiCorreoContrato,
  apiEquipoBloquear,
  apiEquipoDesbloquear,
  apiEquipoEliminar,
  apiLicenciaConfirmarTitularidad,
  apiLicenciaExtenderVigencia,
  apiLicenciaReactivar,
  apiLicenciaRestarDias,
  apiLicenciaSolicitarTitularidad,
  apiLicenciasSincronizar,
  apiSolicitudAutorizar,
  apiSolicitudRevocar,
  apiTenantDetalle,
  apiTenantMaxDevices,
  apiTenantsList,
} from "../settings/supportApi";
import {
  EMAIL_REGEX,
  sanitizarEmail,
  sanitizarTexto,
  sanitizarMultilinea,
  validarSinInyeccion,
} from "../settings/inputSecurity";

const formatFecha = (v) => (v ? new Date(v).toLocaleString("es-MX") : "—");
const getId = (row) => row?.id || row?.uId;
const getTenant = (detalle) => detalle?.tenant || detalle?.oTenant;
const getTenantId = (detalle) => getId(getTenant(detalle));

const TenantsPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busqueda, setBusqueda] = useState("");
  const [estatus, setEstatus] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const [reactivarOpen, setReactivarOpen] = useState(false);
  const [selLic, setSelLic] = useState(null);
  const [reactivarDias, setReactivarDias] = useState("");
  const [reactivarMotivo, setReactivarMotivo] = useState("");

  const [restarOpen, setRestarOpen] = useState(false);
  const [restarDias, setRestarDias] = useState("");
  const [restarMotivo, setRestarMotivo] = useState("");

  const [extenderOpen, setExtenderOpen] = useState(false);
  const [extenderModo, setExtenderModo] = useState("dias");
  const [extenderDias, setExtenderDias] = useState("30");
  const [extenderFecha, setExtenderFecha] = useState("");
  const [extenderMotivo, setExtenderMotivo] = useState("");
  const [extenderRefPago, setExtenderRefPago] = useState("");

  const [eliminarOpen, setEliminarOpen] = useState(false);
  const [selEquipo, setSelEquipo] = useState(null);

  const [correoOpen, setCorreoOpen] = useState(false);
  const [correoTo, setCorreoTo] = useState("");

  const [titularidadOpen, setTitularidadOpen] = useState(false);
  const [selTit, setSelTit] = useState(null);
  const [titularidadEnviado, setTitularidadEnviado] = useState(false);
  const [titularidadDestinatario, setTitularidadDestinatario] = useState("");
  const [titularidadExpira, setTitularidadExpira] = useState(null);
  const [titularidadCodigo, setTitularidadCodigo] = useState("");
  const [titularidadEnviando, setTitularidadEnviando] = useState(false);
  const [titularidadConfirmando, setTitularidadConfirmando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    const busquedaLimpia = sanitizarTexto(busqueda, 120);
    const estatusLimpio = sanitizarTexto(estatus, 40);
    apiTenantsList({ busqueda: busquedaLimpia || undefined, estatus: estatusLimpio || undefined, pagina, tamano })
      .then((r) => {
        setRows(r.items || []);
        setTotal(r.total || r.items?.length || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [busqueda, estatus, pagina, tamano]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirDetalle = (tenantId) => {
    setDetalleOpen(true);
    setDetalleLoading(true);
    setDetalle(null);
    apiTenantDetalle(tenantId)
      .then(setDetalle)
      .catch((err) => setError(err.message))
      .finally(() => setDetalleLoading(false));
  };

  const refrescarDetalle = () => {
    const id = getTenantId(detalle);
    if (!id) return;
    apiTenantDetalle(id)
      .then(setDetalle)
      .catch(() => {});
  };

  const act = async (fn, msg) => {
    setError("");
    setOk("");
    try {
      const result = await fn();
      setOk(result?.mensaje || msg);
      refrescarDetalle();
    } catch (err) {
      setError(err.message || msg);
    }
  };

  const guardarReactivar = () => {
    const motivo = sanitizarMultilinea(reactivarMotivo, 500);
    if (!motivo.trim()) {
      setError("El motivo es requerido.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Motivo", valor: reactivarMotivo }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    return act(
      () =>
        apiLicenciaReactivar(getTenantId(detalle), {
          tenantActivationLicenseId: selLic?.tenantActivationLicenseId || null,
          tenantLicenseId: selLic?.tenantLicenseId || null,
          diasExtension: reactivarDias ? Number(reactivarDias) : null,
          motivo,
        }),
      "Licencia re-activada."
    ).finally(() => {
      setReactivarOpen(false);
      setSelLic(null);
      setReactivarDias("");
      setReactivarMotivo("");
    });
  };

  const guardarRestar = () => {
    const motivo = sanitizarMultilinea(restarMotivo, 500);
    if (!motivo.trim() || !(Number(restarDias) > 0)) {
      setError("Motivo y días válidos son requeridos.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Motivo", valor: restarMotivo }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    return act(
      () =>
        apiLicenciaRestarDias(getTenantId(detalle), {
          tenantActivationLicenseId: selLic?.tenantActivationLicenseId || null,
          tenantLicenseId: selLic?.tenantLicenseId || null,
          diasRestar: restarDias ? Number(restarDias) : null,
          motivo,
        }),
      "Días restados a la licencia."
    ).finally(() => {
      setRestarOpen(false);
      setSelLic(null);
      setRestarDias("");
      setRestarMotivo("");
    });
  };

  const nuevoVencimientoExtender = () => {
    if (extenderModo === "fecha") {
      const t = new Date(extenderFecha).getTime();
      return Number.isNaN(t) ? null : new Date(t);
    }
    const dias = Number(extenderDias);
    return dias > 0 ? new Date(Date.now() + dias * 86400000) : null;
  };

  const guardarExtender = () => {
    const motivo = sanitizarMultilinea(extenderMotivo, 500);
    if (!motivo.trim()) {
      setError("El motivo es requerido.");
      return;
    }
    const bloqueo = validarSinInyeccion([
      { etiqueta: "Motivo", valor: extenderMotivo },
      { etiqueta: "Referencia de pago", valor: extenderRefPago },
    ]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    let fechaIso = null;
    if (extenderModo === "fecha") {
      const t = new Date(extenderFecha).getTime();
      if (Number.isNaN(t)) {
        setError("Indica una fecha de vencimiento válida.");
        return;
      }
      if (t <= Date.now()) {
        setError("La fecha de vencimiento debe ser futura.");
        return;
      }
      fechaIso = new Date(t).toISOString();
    } else if (!(Number(extenderDias) > 0)) {
      setError("Indica días de extensión mayores a 0.");
      return;
    }
    return act(
      () =>
        apiLicenciaExtenderVigencia(getTenantId(detalle), {
          tenantActivationLicenseId: selLic?.tenantActivationLicenseId || null,
          tenantLicenseId: selLic?.tenantLicenseId || null,
          diasExtension: extenderModo === "dias" ? Number(extenderDias) : null,
          fechaVencimientoUtc: fechaIso,
          motivo,
          referenciaPago: sanitizarTexto(extenderRefPago, 120) || null,
        }),
      "Vigencia de licencia extendida."
    ).finally(() => {
      setExtenderOpen(false);
      setSelLic(null);
      setExtenderDias("30");
      setExtenderFecha("");
      setExtenderMotivo("");
      setExtenderRefPago("");
    });
  };

  const confirmarEliminar = () =>
    act(() => apiEquipoEliminar(getTenantId(detalle), selEquipo.devicePublicId), "Equipo eliminado.").finally(() => {
      setEliminarOpen(false);
      setSelEquipo(null);
    });

  const enviarCorreoContrato = () => {
    const destino = sanitizarEmail(correoTo, 180);
    if (destino && !EMAIL_REGEX.test(destino)) {
      setError("Correo destinatario inválido.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Destinatario", valor: correoTo }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    return act(
      () =>
        apiCorreoContrato(getTenantId(detalle), {
          toEmail: destino || undefined,
        }),
      "Correo de contrato enviado."
    ).finally(() => {
      setCorreoOpen(false);
    });
  };

  const abrirTitularidad = (licencia) => {
    setSelTit(licencia);
    setTitularidadCodigo("");
    setTitularidadEnviado(false);
    setTitularidadDestinatario("");
    setTitularidadExpira(null);
    setTitularidadOpen(true);
  };

  const enviarTitularidad = async () => {
    setError("");
    setOk("");
    setTitularidadEnviando(true);
    try {
      const r = await apiLicenciaSolicitarTitularidad(getTenantId(detalle), {
        tenantActivationLicenseId: selTit?.tenantActivationLicenseId || null,
        tenantLicenseId: selTit?.tenantLicenseId || null,
      });
      setOk(r?.mensaje || "Código de validación enviado.");
      setTitularidadDestinatario(r?.destinatario || "");
      setTitularidadExpira(r?.expiresAtUtc || null);
      setTitularidadEnviado(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setTitularidadEnviando(false);
    }
  };

  const confirmarTitularidad = () =>
    act(
      () =>
        apiLicenciaConfirmarTitularidad(getTenantId(detalle), {
          tenantActivationLicenseId: selTit?.tenantActivationLicenseId || null,
          tenantLicenseId: selTit?.tenantLicenseId || null,
          codigo: titularidadCodigo,
        }),
      "Titularidad confirmada."
    ).finally(() => {
      setTitularidadOpen(false);
      setSelTit(null);
      setTitularidadCodigo("");
      setTitularidadEnviado(false);
      setTitularidadDestinatario("");
      setTitularidadExpira(null);
    });

  const columns = [
    { field: "tenantKey", headerName: "Clave", width: 140 },
    { field: "razonSocial", headerName: "Razón social", width: 220, flex: 1 },
    { field: "nombreComercial", headerName: "Comercial", width: 160 },
    { field: "emailContacto", headerName: "Contacto", width: 180 },
    {
      field: "estatus",
      headerName: "Estatus",
      width: 130,
      renderCell: (p) => <Chip size="small" color={p.row.activo ? "success" : "error"} label={p.value} />,
    },
    { field: "databaseName", headerName: "BD", width: 130 },
    { field: "fechaAltaUtc", headerName: "Alta", width: 140, valueFormatter: (v) => formatFecha(v) },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="Ver detalle">
          <IconButton size="small" onClick={() => abrirDetalle(getId(p.row))}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Licencias y Tenants
        </Typography>
        <Button component="a" href="#/resumen" variant="outlined" startIcon={<HomeIcon />}>
          Main
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" onClose={() => setOk("")} sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          label="Buscar"
          variant="outlined"
          size="small"
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setPagina(1);
          }}
          sx={{ width: 300 }}
        />
        <TextField
          label="Estatus"
          variant="outlined"
          size="small"
          value={estatus}
          onChange={(e) => {
            setEstatus(e.target.value);
            setPagina(1);
          }}
          sx={{ width: 180 }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={cargar}>
          Refrescar
        </Button>
      </Stack>

      <Box sx={{ height: 560 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={getId}
          loading={loading}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          pagination
          paginationMode="server"
          rowCount={total}
          page={pagina - 1}
          pageSize={tamano}
          onPageChange={(p) => setPagina(p + 1)}
          onPageSizeChange={(s) => {
            setTamano(s);
            setPagina(1);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>

      <DetalleTenant
        open={detalleOpen}
        loading={detalleLoading}
        detalle={detalle}
        onClose={() => setDetalleOpen(false)}
        onBlock={(devicePublicId, payload) =>
          act(() => apiEquipoBloquear(getTenantId(detalle), devicePublicId, payload), "No se pudo bloquear.")
        }
        onUnblock={(devicePublicId) =>
          act(() => apiEquipoDesbloquear(getTenantId(detalle), devicePublicId), "No se pudo desbloquear.")
        }
        onAutorizar={(solicitudId) =>
          act(() => apiSolicitudAutorizar(getTenantId(detalle), solicitudId), "No se pudo autorizar.")
        }
        onRevocar={(solicitudId) =>
          act(() => apiSolicitudRevocar(getTenantId(detalle), solicitudId), "No se pudo revocar.")
        }
        onMaxDevices={(payload) =>
          act(() => apiTenantMaxDevices(getTenantId(detalle), payload), "No se pudo actualizar MaxDevices.")
        }
        onReactivar={(licencia) => {
          setSelLic(licencia);
          setReactivarDias("");
          setReactivarMotivo("");
          setReactivarOpen(true);
        }}
        onRestar={(licencia) => {
          setSelLic(licencia);
          setRestarDias("");
          setRestarMotivo("");
          setRestarOpen(true);
        }}
        onExtender={(licencia) => {
          setSelLic(licencia);
          setExtenderModo("dias");
          setExtenderDias("30");
          setExtenderFecha("");
          setExtenderMotivo("");
          setExtenderRefPago("");
          setExtenderOpen(true);
        }}
        onSincronizar={() => act(() => apiLicenciasSincronizar(getTenantId(detalle)), "Licencias sincronizadas.")}
        onValidarTitularidad={(licencia) => abrirTitularidad(licencia)}
        onEliminarEquipo={(equipo) => {
          setSelEquipo(equipo);
          setEliminarOpen(true);
        }}
        onCorreoContrato={() => {
          setCorreoTo("");
          setCorreoOpen(true);
        }}
      />

      <Dialog open={reactivarOpen} onClose={() => setReactivarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Re-activar licencia</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {selLic?.licenseCode || selLic?.licenseKey} ({selLic?.estatus})
            </Typography>
            <TextField
              label="Días de extensión (opcional)"
              type="number"
              size="small"
              fullWidth
              value={reactivarDias}
              onChange={(e) => setReactivarDias(e.target.value)}
            />
            <TextField
              label="Motivo"
              size="small"
              multiline
              rows={2}
              fullWidth
              value={reactivarMotivo}
              onChange={(e) => setReactivarMotivo(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivarOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={guardarReactivar}
            disabled={!reactivarMotivo.trim()}
          >
            Reactivar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restarOpen} onClose={() => setRestarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Restar días a la licencia</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {selLic?.licenseCode || selLic?.licenseKey} ({selLic?.estatus})
            </Typography>
            <TextField
              label="Días a restar"
              type="number"
              size="small"
              fullWidth
              value={restarDias}
              onChange={(e) => setRestarDias(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Motivo"
              size="small"
              multiline
              rows={2}
              fullWidth
              value={restarMotivo}
              onChange={(e) => setRestarMotivo(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestarOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={guardarRestar}
            disabled={!restarMotivo.trim() || !(Number(restarDias) > 0)}
          >
            Restar días
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={extenderOpen} onClose={() => setExtenderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Extender vigencia de licencia</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {selLic?.licenseCode || selLic?.licenseKey} ({selLic?.estatus})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vence actual: {formatFecha(selLic?.expiresAtUtc)}
              {nuevoVencimientoExtender() && (
                <> → Nuevo: {formatFecha(nuevoVencimientoExtender().toISOString())}</>
              )}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={extenderModo === "dias" ? "contained" : "outlined"}
                onClick={() => setExtenderModo("dias")}
              >
                Por días
              </Button>
              <Button
                size="small"
                variant={extenderModo === "fecha" ? "contained" : "outlined"}
                onClick={() => setExtenderModo("fecha")}
              >
                Fecha explícita
              </Button>
            </Stack>
            {extenderModo === "dias" ? (
              <TextField
                label="Días de extensión"
                type="number"
                size="small"
                fullWidth
                value={extenderDias}
                onChange={(e) => setExtenderDias(e.target.value)}
                inputProps={{ min: 1 }}
              />
            ) : (
              <TextField
                label="Nueva fecha de vencimiento (UTC)"
                type="datetime-local"
                size="small"
                fullWidth
                value={extenderFecha}
                onChange={(e) => setExtenderFecha(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Referencia de pago (folio/reporte, opcional)"
              size="small"
              fullWidth
              value={extenderRefPago}
              onChange={(e) => setExtenderRefPago(e.target.value)}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="Motivo"
              size="small"
              multiline
              rows={2}
              fullWidth
              value={extenderMotivo}
              onChange={(e) => setExtenderMotivo(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtenderOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={guardarExtender}
            disabled={!extenderMotivo.trim()}
          >
            Extender vigencia
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={eliminarOpen} onClose={() => setEliminarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar equipo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se eliminará físicamente el equipo{" "}
            <b>{selEquipo?.nombreEquipo || selEquipo?.devicePublicId}</b> y se liberará el cupo MaxDevices de la
            licencia. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminarOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarEliminar}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={correoOpen} onClose={() => setCorreoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Correo con datos de contrato</DialogTitle>
        <DialogContent>
          <TextField
            label="Destinatario"
            type="email"
            size="small"
            fullWidth
            value={correoTo}
            onChange={(e) => setCorreoTo(e.target.value)}
            placeholder={getTenant(detalle)?.emailContacto || "correo@ejemplo.com"}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            Si se deja vacío se usará el correo de contacto del tenant: {getTenant(detalle)?.emailContacto || "—"}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorreoOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<MailOutlineIcon />} onClick={enviarCorreoContrato}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={titularidadOpen} onClose={() => setTitularidadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Validar titularidad de la licencia</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {selTit?.licenseCode || selTit?.licenseKey} ({selTit?.estatus})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Correo registrado del dueño: <b>{selTit?.issuedToEmail || "No registrado"}</b>
            </Typography>
            {!titularidadEnviado ? (
              <Alert severity="info">
                Se enviará al correo del dueño un código de 6 dígitos. El cliente deberá reportarlo a soporte y lo
                ingresarás aquí para confirmar que es dueño de la cuenta.
              </Alert>
            ) : (
              <>
                <Alert severity="success">
                  Código enviado a <b>{titularidadDestinatario}</b>. Expira el {formatFecha(titularidadExpira)}.
                </Alert>
                <TextField
                  label="Código reportado por el cliente"
                  size="small"
                  fullWidth
                  autoFocus
                  value={titularidadCodigo}
                  onChange={(e) => setTitularidadCodigo(e.target.value.replace(/[^0-9]/g, ""))}
                  inputProps={{ maxLength: 6 }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTitularidadOpen(false)}>Cancelar</Button>
          {!titularidadEnviado ? (
            <Button
              variant="contained"
              startIcon={<MailOutlineIcon />}
              onClick={enviarTitularidad}
              disabled={titularidadEnviando}
            >
              Enviar código
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<VerifiedUserIcon />}
              onClick={confirmarTitularidad}
              disabled={!titularidadCodigo.trim() || titularidadConfirmando}
            >
              Confirmar titularidad
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MaxDevicesCell = ({ licencia, onApply }) => {
  const [valor, setValor] = useState(licencia.maxDevices);
  const editar = licencia.estatus.includes("ACTIVA") || licencia.estatus.includes("ACTIVE") || licencia.estatus.includes("ACTIVO");
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        type="number"
        value={valor}
        disabled={!editar}
        onChange={(e) => setValor(Number(e.target.value))}
        sx={{ width: 90 }}
      />
      {editar && (
        <Button
          size="small"
          variant="contained"
          disabled={valor === licencia.maxDevices}
          onClick={() =>
            onApply({
              tenantActivationLicenseId: licencia.tenantActivationLicenseId || null,
              tenantLicenseId: licencia.tenantLicenseId || null,
              maxDevices: valor,
            })
          }
        >
          Aplicar
        </Button>
      )}
    </Stack>
  );
};

const DetalleTenant = ({ open, loading, detalle, onClose, onBlock, onUnblock, onAutorizar, onRevocar, onMaxDevices, onReactivar, onRestar, onExtender, onSincronizar, onEliminarEquipo, onCorreoContrato, onValidarTitularidad }) => {
  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} fullScreen>
        <AppBar sx={{ position: "sticky", background: "#0b2a4a" }}>
          <Toolbar>
            <Tooltip title="Regresar al listado">
              <IconButton edge="start" color="inherit" onClick={onClose} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Cargando detalle…
            </Typography>
            <Button color="inherit" component="a" href="#/resumen" startIcon={<HomeIcon />} sx={{ textTransform: "none" }}>
              Main
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ pt: 3 }}>
          <LinearProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!detalle) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle no disponible</DialogTitle>
        <DialogContent>
          <Alert severity="warning">No se pudo cargar el detalle del tenant. Intenta refrescar el listado y abrirlo nuevamente.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const t = getTenant(detalle);

  if (!t) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle no disponible</DialogTitle>
        <DialogContent>
          <Alert severity="warning">La respuesta del servidor no incluyó los datos principales del tenant.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar sx={{ position: "sticky", background: "#0b2a4a" }}>
        <Toolbar>
          <Tooltip title="Regresar al listado">
            <IconButton edge="start" color="inherit" onClick={onClose} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {t.razonSocial || "Detalle del tenant"}
              </Typography>
              <Chip size="small" label={t.tenantKey || "Sin TenantKey"} sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "white" }} />
            </Stack>
            <Typography variant="body2" noWrap sx={{ opacity: 0.82 }}>
              {t.nombreComercial || "Sin nombre comercial"} · {t.rfc || "—"} · BD: {t.databaseName || "—"}
            </Typography>
          </Box>
          <Button color="inherit" component="a" href="#/resumen" startIcon={<HomeIcon />} sx={{ textTransform: "none", mr: 1 }}>
            Main
          </Button>
          <Button color="inherit" startIcon={<MailOutlineIcon />} onClick={onCorreoContrato} sx={{ textTransform: "none" }}>
            Correo
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent dividers sx={{ background: "#f6f8fb" }}>
        <Seccion titulo="Licencias y cupo de equipos (MaxDevices)">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Alinear TenantActivationLicenses con TenantLicenses del tenant (fechas y estatus)">
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onSincronizar}>
                Sincronizar
              </Button>
            </Tooltip>
          </Stack>
          {(detalle.licenciasActivacion || []).length === 0 && (detalle.licencias || []).length === 0 && (
            <Typography color="text.secondary">Sin licencias registradas.</Typography>
          )}
          {[...(detalle.licenciasActivacion || []), ...(detalle.licencias || [])].map((l, i) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={l.licenseCode || l.licenseKey} variant="outlined" />
                <Chip size="small" label={l.estatus} color={l.activo ? "success" : "default"} />
                {l.ownershipVerifiedAtUtc && (
                  <Chip
                    size="small"
                    color="success"
                    icon={<VerifiedUserIcon />}
                    label={`Dueño verificado ${new Date(l.ownershipVerifiedAtUtc).toLocaleString("es-MX")}`}
                  />
                )}
                <Typography variant="body2">
                  Vigencia: {formatFecha(l.validFromUtc)} → {formatFecha(l.expiresAtUtc)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Usuarios: {l.maxUsers}
                </Typography>
                <Tooltip title="Validar por correo que el cliente es el dueño de la licencia">
                  <Button size="small" variant="outlined" color="info" startIcon={<VerifiedUserIcon />} onClick={() => onValidarTitularidad(l)}>
                    Verificar titularidad
                  </Button>
                </Tooltip>
                {l.plan && (
                  <Typography variant="body2" color="text.secondary">
                    Plan: {l.plan.nombre} ({l.plan.planKey}) · ${l.plan.precioBase.toLocaleString("es-MX")} {l.plan.moneda}
                  </Typography>
                )}
                {!l.activo && (
                  <Button size="small" variant="outlined" color="success" onClick={() => onReactivar(l)}>
                    Reactivar
                  </Button>
                )}
                <Button size="small" variant="outlined" color="success" onClick={() => onExtender(l)}>
                  Extender vigencia
                </Button>
                <Button size="small" variant="outlined" color="warning" onClick={() => onRestar(l)}>
                  Restar días
                </Button>
              </Stack>
              <MaxDevicesCell licencia={l} onApply={onMaxDevices} />
            </Box>
          ))}
        </Seccion>

        <Seccion titulo="Plan y suscripción">
          {!detalle.suscripcion && <Typography color="text.secondary">Sin suscripción activa.</Typography>}
          {detalle.suscripcion && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                {detalle.suscripcion.plan ? (
                  <Chip
                    label={`${detalle.suscripcion.plan.nombre} (${detalle.suscripcion.plan.planKey}) · $${Number(detalle.suscripcion.plan.precioBase).toLocaleString("es-MX")} ${detalle.suscripcion.plan.moneda}`}
                    color="primary"
                    variant="outlined"
                  />
                ) : (
                  <Chip label="Sin plan asociado" variant="outlined" />
                )}
                <Chip size="small" label={detalle.suscripcion.estatus} color={detalle.suscripcion.activo ? "success" : "default"} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Período {detalle.suscripcion.billingPeriod} · Inicio {formatFecha(detalle.suscripcion.startsAtUtc)} · Fin{" "}
                {formatFecha(detalle.suscripcion.endsAtUtc)} · Siguiente cargo{" "}
                {formatFecha(detalle.suscripcion.nextBillingAtUtc)} · Renovación automática:{" "}
                {detalle.suscripcion.autoRenew ? "Sí" : "No"}
              </Typography>
            </>
          )}
        </Seccion>

        <Seccion titulo="Equipos autorizados">
          {(detalle.equipos || []).length === 0 && <Typography color="text.secondary">Sin equipos.</Typography>}
          {(detalle.equipos || []).map((eq) => (
            <Stack key={getId(eq)} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2">
                  {eq.nombreEquipo || eq.sDevicePublicId} ({eq.plataforma || "—"})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {eq.devicePublicId} · {eq.ultimaIp || "—"} · {eq.ultimoPais || "—"} ·{" "}
                  {formatFecha(eq.ultimaConexionUtc)}
                </Typography>
              </Box>
              <Chip size="small" label={eq.estatus} />
              {eq.activo ? (
                <Tooltip title="Bloquear equipo">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onBlock(eq.devicePublicId, { motivo: "Bloqueo manual por soporte" })}
                  >
                    <BlockIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Desbloquear equipo">
                  <IconButton size="small" color="success" onClick={() => onUnblock(eq.devicePublicId)}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Eliminar equipo">
                <IconButton size="small" color="default" onClick={() => onEliminarEquipo(eq)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Seccion>

        <Seccion titulo="Solicitudes de acceso móvil">
          {(detalle.solicitudesAcceso || []).length === 0 && (
            <Typography color="text.secondary">Sin solicitudes pendientes.</Typography>
          )}
          {(detalle.solicitudesAcceso || []).map((s) => (
            <Stack key={getId(s)} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2">
                  {s.nombreDispositivo || s.devicePublicId} · {s.correoSolicitante || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Licencia {s.licenseCode} · solicitado {formatFecha(s.requestedAtUtc)}
                </Typography>
              </Box>
              <Chip size="small" label={s.estatus} />
              {s.estatus === "PENDING_APPROVAL" && (
                <>
                  <IconButton size="small" color="success" onClick={() => onAutorizar(getId(s))}>
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => onRevocar(getId(s))}>
                    <RemoveCircleIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          ))}
        </Seccion>

        <Seccion titulo="Saldos de comunicación">
          {(detalle.saldosComunicacion || []).length === 0 && (
            <Typography color="text.secondary">Sin canales configurados.</Typography>
          )}
          {(detalle.saldosComunicacion || []).map((c) => (
            <Chip
              key={getId(c)}
              label={`${c.canal} (${c.llave}): saldo ${c.saldoConsumo} / límite ${c.limiteAlerta} / total ${c.consumoTotal}`}
              variant="outlined"
              color={c.bajoSaldo ? "warning" : "default"}
              sx={{ mr: 1 }}
            />
          ))}
        </Seccion>

        <Seccion titulo="Pagos registrados">
          {(detalle.pagos || []).length === 0 && <Typography color="text.secondary">Sin pagos registrados.</Typography>}
          {(detalle.pagos || []).map((p) => (
            <Typography key={getId(p)} variant="body2">
              {p.periodo} · ${p.monto.toLocaleString("es-MX")} · {p.metodo} · {p.referencia} ·{" "}
              {formatFecha(p.pagadoAtUtc)}
            </Typography>
          ))}
        </Seccion>
      </DialogContent>
    </Dialog>
  );
};

const Seccion = ({ titulo, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {titulo}
    </Typography>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Box>
);

export default TenantsPage;